import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  DeleteFilled,
  DownloadOutlined,
  EditFilled,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  SwapOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { showError, showSuccess } from '@components/notification';
import { usePermission } from '@contexts/Auth/UsePermission';
import {
  apiDeleteSimCard,
  apiExportSimCards,
  apiGetSimCards,
  apiUpdateSimCardStatus
} from '@services/ManageSim/SimServices';
import SimImportModal from './SimImportModal';
import SimAssignmentModal from './SimAssignmentModal';

const { RangePicker } = DatePicker;

const controlStyle = { width: '100%', height: 36 };
const primaryButtonStyle = {
  height: 36,
  color: '#fff',
  backgroundColor: '#0284c7',
  borderColor: '#0284c7'
};

const STATUS_OPTIONS = [
  { value: 'STORED', label: 'Lưu kho' },
  { value: 'ACTIVE', label: 'Hoạt động' },
  { value: 'SUSPENDED', label: 'Tạm ngưng' },
  { value: 'DAMAGED', label: 'Hư hỏng' },
  { value: 'LOST', label: 'Thất lạc' },
  { value: 'DEACTIVATED', label: 'Ngừng hoạt động' }
];

const SIM_TYPE_OPTIONS = [
  { value: 'PHYSICAL', label: 'SIM vật lý' },
  { value: 'ESIM', label: 'eSIM' }
];

const statusDisplay = {
  STORED: { color: 'var(--link)', label: 'Lưu kho' },
  ACTIVE: { color: 'success', label: 'Hoạt động' },
  SUSPENDED: { color: 'warning', label: 'Tạm ngưng' },
  DAMAGED: { color: 'error', label: 'Hư hỏng' },
  LOST: { color: 'error', label: 'Thất lạc' },
  DEACTIVATED: { color: 'default', label: 'Ngừng hoạt động' }
};

const initialFilters = {
  keyword: '',
  status: undefined,
  carrier: undefined,
  sim_type: undefined,
  technology: '',
  plan_name: '',
  activation_range: null
};

const SimCardList = () => {
  const navigate = useNavigate();
  const { sim } = usePermission();
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importVisible, setImportVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusModal, setStatusModal] = useState({
    open: false,
    record: null,
    status: undefined,
    notes: ''
  });
  const [statusLoading, setStatusLoading] = useState(false);
  const [assignmentRecord, setAssignmentRecord] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const range = appliedFilters.activation_range;
      const response = await apiGetSimCards({
        page,
        size: pageSize,
        keyword: appliedFilters.keyword || undefined,
        status: appliedFilters.status,
        carrier: appliedFilters.carrier,
        sim_type: appliedFilters.sim_type,
        technology: appliedFilters.technology || undefined,
        plan_name: appliedFilters.plan_name || undefined,
        activation_from: range?.[0]?.format('YYYY-MM-DD'),
        activation_to: range?.[1]?.format('YYYY-MM-DD')
      });
      const rows = response.data?.data || [];
      setData(
        rows.map((item, index) => ({
          ...item,
          key: item.id,
          stt: (page - 1) * pageSize + index + 1
        }))
      );
      setTotal(response.data?.meta?.total || 0);
    } catch (error) {
      showError(error?.response?.data?.message || 'Không thể tải danh sách SIM.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, appliedFilters]);

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Xóa dữ liệu SIM',
      content: `Bạn có chắc chắn muốn xóa SIM có ICCID ${record.iccid}?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await apiDeleteSimCard(record.id);
          showSuccess({ message: 'Xóa SIM thành công.' });
          if (data.length === 1 && page > 1) {
            setPage(page - 1);
          } else {
            fetchData();
          }
        } catch (error) {
          showError(error?.response?.data?.message || 'Không thể xóa SIM.');
        }
      }
    });
  };

  const openStatusModal = (record) => {
    setStatusModal({
      open: true,
      record,
      status: record.status,
      notes: record.notes || ''
    });
  };

  const handleUpdateStatus = async () => {
    try {
      setStatusLoading(true);
      await apiUpdateSimCardStatus(statusModal.record.id, {
        status: statusModal.status,
        notes: statusModal.notes
      });
      showSuccess({ message: 'Cập nhật trạng thái SIM thành công.' });
      setStatusModal({ open: false, record: null, status: undefined, notes: '' });
      fetchData();
    } catch (error) {
      showError(error?.response?.data?.message || 'Không thể cập nhật trạng thái SIM.');
    } finally {
      setStatusLoading(false);
    }
  };

  const columns = [
    { title: 'STT', dataIndex: 'stt', width: 70, fixed: 'left' },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 145,
      render: (value) => {
        const display = statusDisplay[value] || { color: 'default', label: value };
        return <Tag color={display.color}>{display.label}</Tag>;
      }
    },
    { title: 'ICICD', dataIndex: 'iccid', width: 205 },
    { title: 'IMSI', dataIndex: 'imsi', width: 175 },
    {
      title: 'Số máy',
      dataIndex: 'phone_number',
      width: 145,
      render: (value) => value || '-'
    },
    { title: 'Nhà mạng', dataIndex: 'carrier', width: 120 },
    {
      title: 'Ngày hòa mạng',
      dataIndex: 'activation_date',
      width: 145,
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-')
    },
    {
      title: 'Gói cước',
      dataIndex: 'plan_name',
      width: 125,
      render: (value) => value || '-'
    },
    {
      title: 'Công nghệ',
      dataIndex: 'technology',
      width: 120,
      render: (value) => value || '-'
    },
    {
      title: 'Loại sim',
      dataIndex: 'sim_type',
      width: 115,
      render: (value) => SIM_TYPE_OPTIONS.find((item) => item.value === value)?.label || value
    },
    {
      title: 'Thiết bị đang gắn',
      dataIndex: 'current_assignment',
      width: 210,
      render: (assignment) =>
        assignment?.device ? (
          <div>
            <Typography.Link>
              {assignment.device.serial_no || assignment.device.device_name}
            </Typography.Link>
            <div style={{ color: '#64748b', fontSize: 12 }}>IMEI: {assignment.device.imei}</div>
          </div>
        ) : (
          <Typography.Text type="secondary">Chưa gắn</Typography.Text>
        )
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      width: 180,
      render: (value) => value || '-'
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          {sim.edit && (
            <Tooltip title="Cập nhật dữ liệu SIM">
              <Button
                type="text"
                shape="circle"
                icon={<EditFilled style={{ color: '#0284c7', fontSize: 18 }} />}
                onClick={() => navigate(`/sim-cards/${record.id}/edit`)}
              />
            </Tooltip>
          )}
          {sim.edit && (
            <Tooltip title="Gán/chuyển thiết bị và xem lịch sử">
              <Button
                type="text"
                shape="circle"
                icon={<SwapOutlined style={{ color: '#0284c7', fontSize: 18 }} />}
                onClick={() => setAssignmentRecord(record)}
              />
            </Tooltip>
          )}
          {sim.edit && (
            <Tooltip title="Cập nhật trạng thái">
              <Button
                type="text"
                shape="circle"
                icon={<SyncOutlined style={{ color: '#0284c7', fontSize: 18 }} />}
                onClick={() => openStatusModal(record)}
              />
            </Tooltip>
          )}
          {sim.delete && (
            <Tooltip title="Xóa SIM">
              <Button
                danger
                type="text"
                shape="circle"
                icon={<DeleteFilled style={{ fontSize: 18 }} />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPage(1);
    setAppliedFilters(initialFilters);
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const range = appliedFilters.activation_range;
      const response = await apiExportSimCards({
        keyword: appliedFilters.keyword || undefined,
        status: appliedFilters.status,
        carrier: appliedFilters.carrier,
        sim_type: appliedFilters.sim_type,
        technology: appliedFilters.technology || undefined,
        plan_name: appliedFilters.plan_name || undefined,
        activation_from: range?.[0]?.format('YYYY-MM-DD'),
        activation_to: range?.[1]?.format('YYYY-MM-DD')
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `danh_sach_SIM_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError(error?.response?.data?.message || 'Không thể xuất file danh sách SIM.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <SimImportModal
        visible={importVisible}
        onCancel={() => setImportVisible(false)}
        onSuccess={() => {
          setImportVisible(false);
          fetchData();
        }}
      />
      <SimAssignmentModal
        open={Boolean(assignmentRecord)}
        record={assignmentRecord}
        onCancel={() => setAssignmentRecord(null)}
        onSuccess={() => {
          setAssignmentRecord(null);
          fetchData();
        }}
      />
      <Modal
        title="Cập nhật trạng thái SIM"
        open={statusModal.open}
        okText="Cập nhật"
        cancelText="Hủy"
        confirmLoading={statusLoading}
        okButtonProps={{ style: primaryButtonStyle }}
        onOk={handleUpdateStatus}
        onCancel={() =>
          setStatusModal({ open: false, record: null, status: undefined, notes: '' })
        }>
        <Typography.Paragraph>
          ICICD: <Typography.Text strong>{statusModal.record?.iccid}</Typography.Text>
        </Typography.Paragraph>
        <Select
          value={statusModal.status}
          options={STATUS_OPTIONS}
          style={controlStyle}
          onChange={(status) => setStatusModal((current) => ({ ...current, status }))}
        />
        <Typography.Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>
          Ghi chú
        </Typography.Text>
        <Input.TextArea
          rows={3}
          value={statusModal.notes}
          placeholder="Nhập ghi chú trạng thái SIM"
          onChange={(event) =>
            setStatusModal((current) => ({ ...current, notes: event.target.value }))
          }
        />
      </Modal>
      <Row justify="space-between" align="middle" gutter={[12, 12]}>
        <Col>
          <Typography.Title level={4} style={{ marginBottom: 12 }}>
            Quản lý SIM
          </Typography.Title>
        </Col>
        <Col>
          <Space wrap>
            {sim.create && (
              <Button
                icon={<PlusOutlined />}
                style={{
                  color: '#0284c7',
                  height: '36px',
                  backgroundColor: '#eef1f6',
                  borderColor: '#eef1f6',
                  fontSize: '18px'
                }}
                onClick={() => navigate('/sim-cards/add')}>
                Thêm mới
              </Button>
            )}
            {sim.import && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                style={primaryButtonStyle}
                onClick={() => setImportVisible(true)}>
                Nhập file Excel
              </Button>
            )}
            {sim.export && (
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                style={primaryButtonStyle}
                onClick={handleExport}>
                Xuất file Excel
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card bodyStyle={{ padding: 12 }} style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]}>
          <Col xl={6} md={8} xs={24}>
            <Input
              value={filters.keyword}
              placeholder="Nhập ICICD, IMSI, số máy..."
              style={controlStyle}
              onChange={(event) => updateFilter('keyword', event.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          {/* <Col xl={4} md={8} xs={24}>
            <Input
              value={filters.technology}
              placeholder="Nhập công nghệ"
              style={controlStyle}
              onChange={(event) => updateFilter('technology', event.target.value)}
              onPressEnter={handleSearch}
            />
          </Col> */}
          <Col xl={6} md={8} xs={24}>
            <Select
              allowClear
              value={filters.status}
              options={STATUS_OPTIONS}
              placeholder="Chọn trạng thái"
              style={controlStyle}
              onChange={(value) => updateFilter('status', value)}
            />
          </Col>
          <Col xl={6} md={8} xs={24}>
            <Select
              allowClear
              value={filters.carrier}
              placeholder="Chọn nhà mạng"
              style={controlStyle}
              options={['Viettel', 'Vinaphone', 'Mobifone', 'Vietnamobile'].map((value) => ({
                value,
                label: value
              }))}
              onChange={(value) => updateFilter('carrier', value)}
            />
          </Col>
          <Col xl={6} md={8} xs={24}>
            <Select
              allowClear
              value={filters.sim_type}
              options={SIM_TYPE_OPTIONS}
              placeholder="Chọn loại SIM"
              style={controlStyle}
              onChange={(value) => updateFilter('sim_type', value)}
            />
          </Col>
          <Col xl={6} md={8} xs={24}>
            <Input
              value={filters.plan_name}
              placeholder="Nhập gói cước"
              style={controlStyle}
              onChange={(event) => updateFilter('plan_name', event.target.value)}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col xl={6} md={8} xs={24}>
            <RangePicker
              value={filters.activation_range}
              format="DD/MM/YYYY"
              placeholder={['Từ ngày hòa mạng', 'Đến ngày']}
              style={controlStyle}
              onChange={(value) => updateFilter('activation_range', value)}
            />
          </Col>
          <Col flex="none">
            <Space>
              <Button
                icon={<ReloadOutlined />}
                style={{ height: 36, color: '#0284c7', borderColor: '#0284c7' }}
                onClick={handleReset}>
                Đặt lại
              </Button>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                style={primaryButtonStyle}
                onClick={handleSearch}>
                Tìm kiếm
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card
        title={
          <Typography.Text strong style={{ color: '#0284c7' }}>
            Danh sách SIM ({total.toLocaleString('vi-VN')})
          </Typography.Text>
        }
        bodyStyle={{ padding: 0 }}>
        <Table
          bordered
          loading={loading}
          dataSource={data}
          columns={columns}
          scroll={{ x: 1750 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              if (nextSize !== pageSize) {
                setPageSize(nextSize);
                setPage(1);
              } else {
                setPage(nextPage);
              }
            },
            showTotal: (count, range) => `${range[0]}-${range[1]} của ${count} bản ghi`
          }}
        />
      </Card>
    </div>
  );
};

export default SimCardList;
